import {useEffect, useMemo, useState} from 'react';
import {isBrandPortal} from '@netx/core-cms-next/utils/isBrandPortal';
import {parseActions} from '@netx/core-next/utils';
import type {ReactAction} from '@netx/core/bootstrap';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useCustomActionMenus} from '@netx/core-custom-action/hooks';
import {useLocales} from '@netx/core-next/locales/utils/useLocales';
import Netx from '@netx/core/bootstrap';

import {
	cartActionBulkEditAttributes,
	cartActionCategorize,
	cartActionCreatePDF,
	cartActionCreateTMSObjectPackage,
	cartActionCustom,
	cartActionDelete,
	cartActionEmpty,
	cartActionDownload,
	cartActionEditAttributes,
	cartActionExportAttributes,
	cartActionFindReplace,
	cartActionManageArchive,
	cartActionManageExpiration,
	cartActionManageRelationships,
	cartActionManageVisibility,
	cartActionRepurpose,
	cartActionResync,
	cartActionReview,
	cartActionSave,
	cartActionShareMenu,
} from '../actions';
// import {useCurrentCart} from './useCurrentCart';

export type UseCartBarActionsParams = {
	excludeCustomActions?: boolean;
	order?: boolean;
	omit?: string[] | ((action: ReactAction) => boolean);
	pick?: string[] | ((action: ReactAction) => boolean);
};

// For now we will just hardcode the portal actions
// This will be replaced with a more dynamic solution in the future
const portalActions = [
	'cart-current-download',
	'cart-current-create-pdf',
	'cart-create-pdf-download',
	'cart-create-pdf-share-link',
	'cart-export-attributes',
	'cart-export-attributes-download',
	'cart-export-attributes-share-link',
	'cart-current-share-menu',
	'cart-current-empty',
	'cart-current-save',
];

export const useCurrentCartActions = (props?: UseCartBarActionsParams) => {
	// hack for the moment
	const locales = useLocales(() => {
		return {};
	});

	// const {cart} = useCurrentCart();
	const customActionMenus = useCustomActionMenus();

	const [cartHasAssets, setCartHasAssets] = useState(false);

	useEffect(() => {
		const listener = () => {
			const total = Netx.cart.current.getTotal();
			setCartHasAssets(total > 0);
		};
		Netx.cart.current?.on('change reset sync add remove', listener);
		listener();
		return () => {
			Netx.cart.current?.off('change reset sync add remove', listener);
		};
	}, []);

	const getActions = useConstCallback(
		({
			excludeCustomActions,
			omit,
			order = true,
			pick,
		}: UseCartBarActionsParams = {}) => {
			let actions: ReactAction[] = [
				cartActionCategorize(),
				cartActionCreatePDF(),
				cartActionCreateTMSObjectPackage(),
				cartActionDownload(),
				cartActionEditAttributes(),
				cartActionExportAttributes(),
				cartActionBulkEditAttributes(),
				cartActionFindReplace(),
				cartActionManageArchive(),
				cartActionManageExpiration(),
				cartActionManageRelationships(),
				cartActionManageVisibility(),
				cartActionRepurpose(),
				cartActionResync(),
				cartActionReview(),
				cartActionSave(),
				cartActionShareMenu(),

				cartActionEmpty({group: 10000}),
				cartActionDelete({
					group: 10000,
				}),
			];

			// see note above about hardcoding portal actions
			// rather than try and override this file right now or hack even more
			// we will just handle the portal omissions here
			if (isBrandPortal()) {
				actions = actions.filter((action) =>
					portalActions.includes(action.name),
				);
			}

			if (!excludeCustomActions) {
				customActionMenus.data?.selection?.forEach((customAction) => {
					actions.push(
						cartActionCustom(customAction, {
							cb: () => customActionMenus.executeCustomAction(customAction),
							group: 2,
						}),
					);
				});
			}

			return parseActions({
				actions,
				omit,
				order,
				pick,
			});
		},
	);

	const actions = useMemo(
		() => getActions(props),
		[props, cartHasAssets, locales],
	);

	return {
		actions,
		getActions,
		loading: customActionMenus.loading,
	};
};

export default useCurrentCartActions;
